import React, { useState } from "react";
import {
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useLocation, useNavigate } from "react-router-dom";
import { splitNav, findActiveItem } from "../../config/navigation";
import { alpha } from "@mui/material/styles";
import { layout } from "../../theme/tokens";

/**
 * Native-app-style bottom tab bar, shown below the md breakpoint.
 * Overflow items live behind a "More" tab that opens a bottom sheet.
 */
export default function MobileBottomNav({ items }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const { primary, rest } = splitNav(items);
  const active = findActiveItem(items, pathname);
  const activeIndex = primary.findIndex((i) => i.to === active?.to);
  const restIsActive = activeIndex === -1 && Boolean(active);

  const hasMore = rest.length > 0;
  const value = activeIndex !== -1 ? activeIndex : hasMore && restIsActive ? primary.length : false;

  return (
    <>
      <Box
        component="nav"
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (t) => t.zIndex.appBar,
          bgcolor: (t) =>
            t.palette.mode === "dark"
              ? "rgba(19, 27, 46, 0.92)"
              : "rgba(255, 255, 255, 0.92)",
          backdropFilter: "blur(12px)",
          borderTop: 1,
          borderColor: "border.default",
          // Keep the bar clear of the iOS home indicator.
          pb: "env(safe-area-inset-bottom)",
        }}
      >
        <BottomNavigation
          value={value}
          showLabels
          sx={{
            height: layout.bottomNavHeight,
            bgcolor: "transparent",
            "& .MuiBottomNavigationAction-root": {
              minWidth: 0,
              color: "text.secondary",
              gap: 0.25,
              position: "relative",
              transition: "color .15s ease",
              "&.Mui-selected": {
                color: "primary.main",
                "& .MuiSvgIcon-root": {
                  backgroundColor: (t) => alpha(t.palette.primary.main, 0.14),
                  borderRadius: 999,
                },
              },
              "& .MuiSvgIcon-root": {
                px: 1.75,
                py: 0.35,
                borderRadius: 999,
                boxSizing: "content-box",
                transition: "background-color .15s ease",
              },
            },
            "& .MuiBottomNavigationAction-label": {
              fontSize: "0.6875rem",
              fontWeight: 500,
              "&.Mui-selected": { fontSize: "0.6875rem", fontWeight: 600 },
            },
          }}
        >
          {primary.map((item) => {
            const Icon = item.icon;
            return (
              <BottomNavigationAction
                key={item.to}
                label={item.label}
                icon={<Icon sx={{ fontSize: 22 }} />}
                onClick={() => navigate(item.to)}
              />
            );
          })}
          {hasMore && (
            <BottomNavigationAction
              label="More"
              icon={<MoreHorizIcon sx={{ fontSize: 22 }} />}
              onClick={() => setMoreOpen(true)}
            />
          )}
        </BottomNavigation>
      </Box>

      <Drawer
        anchor="bottom"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            pb: "calc(16px + env(safe-area-inset-bottom))",
            maxHeight: "80vh",
          },
        }}
      >
        {/* Grabber, so the sheet reads as draggable the way a native one does. */}
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 0.5 }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 999, bgcolor: "border.strong" }} />
        </Box>
        <Typography variant="subtitle2" sx={{ px: 3, py: 1.5, color: "text.secondary" }}>
          More
        </Typography>
        <Divider />
        <List sx={{ px: 1.5, py: 1 }}>
          {rest.map((item) => {
            const Icon = item.icon;
            const selected = active?.to === item.to;
            return (
              <ListItemButton
                key={item.to}
                selected={selected}
                onClick={() => {
                  navigate(item.to);
                  setMoreOpen(false);
                }}
                sx={{
                  minHeight: 48,
                  gap: 2,
                  color: selected ? "primary.main" : "text.primary",
                }}
              >
                <ListItemIcon sx={{ justifyContent: "center" }}>
                  <Icon sx={{ fontSize: 22 }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: "0.9375rem",
                    fontWeight: selected ? 600 : 500,
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Drawer>
    </>
  );
}
